import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './AssetHistoryComponent.css';
import { useTranslation } from '../redux/TranslationHelper';
import store from '../redux/store';

// 아이콘 컴포넌트들
const PointIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="16"></line>
    <line x1="8" y1="12" x2="16" y2="12"></line>
  </svg>
);

// 새로운 화살표 아이콘 컴포넌트 추가
const ArrowDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

// 환전 아이콘 컴포넌트 추가
const ExchangeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M8 12h8M12 16l4-4-4-4"></path>
  </svg>
);

const AssetHistoryComponent = ({ id, componentProps = {}, style = {}, isPreview = false }) => {
  const { t } = useTranslation();
  
  const { 
    className = "mileage-history-wrapper padding-tb-40",
    title = t("자산 내역"),
    emptyText = t("자산 내역이 없습니다.")
  } = componentProps || {};
  
  const dispatch = useDispatch();
  // 인라인으로 선택자 함수 정의
  const mileageTransactionsState = useSelector((state) => state.withcookie?.mileageTransactions || {
    items: [],
    loading: false,
    error: null,
    pagination: { page: 1, totalPages: 1, totalItems: 0 }
  });
  const { items: mileageHistory, loading, error, pagination } = mileageTransactionsState;
  const isUserLoggedIn = useSelector((state) => state.withcookie?.isUserLoggedIn || false);
  const userInfo = useSelector((state) => state.withcookie?.userInfo || null);
  
  // 페이지 상태
  const [page, setPage] = useState(1);
  
  // 로컬 상태 관리
  const [assetData, setAssetData] = useState([]);
  const [totalSum, setTotalSum] = useState(0);
  const [totalGotMileage, setTotalGotMileage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [apiError, setApiError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // 자산 유형 관련 상태 - 'all' 대신 기본값을 빈 문자열로 변경
  const [assetTypes, setAssetTypes] = useState([]);
  const [selectedAssetType, setSelectedAssetType] = useState('');
  
  // 사용자 보유 자산 목록 관련 상태
  const [userAssetBalances, setUserAssetBalances] = useState([]);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  
  // 출금 요청 폼 상태
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawToAddress, setWithdrawToAddress] = useState('');
  const [withdrawMemo, setWithdrawMemo] = useState('');
  const [withdrawProcessing, setWithdrawProcessing] = useState(false);
  // 선택된 회사 ID 상태 추가
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  // 출금 불가 메시지 상태 추가
  const [withdrawDisabledMessage, setWithdrawDisabledMessage] = useState('');
  // 수수료 및 실제 받을 금액 상태 추가
  const [feeAmount, setFeeAmount] = useState(0);
  const [receiveAmount, setReceiveAmount] = useState(0);

  // 입금 모달 상태 추가
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositAsset, setDepositAsset] = useState(null);

  // 환전 모달 상태 추가
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [exchangeFromAsset, setExchangeFromAsset] = useState(null);
  const [exchangeToAsset, setExchangeToAsset] = useState(null);
  const [exchangeAmount, setExchangeAmount] = useState('');
  const [exchangeReason, setExchangeReason] = useState('');
  const [exchangeProcessing, setExchangeProcessing] = useState(false);
  const [exchangeResult, setExchangeResult] = useState(null);
  const [exchangeFeeAmount, setExchangeFeeAmount] = useState(0);
  const [exchangeReceiveAmount, setExchangeReceiveAmount] = useState(0);
  const [exchangeDisabledMessage, setExchangeDisabledMessage] = useState('');

  // 미리보기 모드에서 사용할 상태를 컴포넌트 최상위 레벨로 이동
  const [previewSelectedAssetType, setPreviewSelectedAssetType] = useState('all');
  const [previewShowWithdrawForm, setPreviewShowWithdrawForm] = useState(false);
  const [previewShowExchangeModal, setPreviewShowExchangeModal] = useState(false);

  useEffect(() => {
    // 미리보기 모드가 아니고 로그인된 상태일 때만 API 호출
    if (!isPreview) {
      if (isUserLoggedIn) {
        // userToken 확인 (Redux나 localStorage에서 가져옴)
        const userToken = localStorage.getItem('userToken');
        
        if (!userToken) {
          console.warn('사용자 토큰이 없습니다. 세션 쿠키로 요청을 시도합니다.');
        }
        
        // 사용자 보유 자산 목록 조회
        loadUserAssetBalances();
        
        // 거래 내역 조회
        loadAssetHistory(page);
      } else {
        setApiError(t('로그인이 필요한 서비스입니다.'));
      }
    }
    
  }, [isPreview, isUserLoggedIn, page, userInfo]);
  
  // 자산 유형 변경 시 회사 ID 초기화 및 출금 가능 여부 확인
  useEffect(() => {
    if (selectedAssetType) {
      const assetInfo = getSelectedAssetTypeInfo();
      
      console.log('자산 유형 변경 시 withdrawal 정보:', {
        assetType: assetInfo?.assetName,
        withdrawal: assetInfo?.withdrawal,
        companySettings: assetInfo?.withdrawal?.companySettings
      });
      
      // 첫 번째 회사를 기본값으로 선택
      if (assetInfo && assetInfo.withdrawal && assetInfo.withdrawal.companySettings && 
          assetInfo.withdrawal.companySettings.length > 0) {
        
        // 출금 가능한 첫 번째 회사 선택
        const enabledCompany = assetInfo.withdrawal.companySettings.find(company => company.enabled);
        
        if (enabledCompany) {
          setSelectedCompanyId(enabledCompany.companyId.toString());
          setWithdrawDisabledMessage('');
        } else {
          setSelectedCompanyId('');
          setWithdrawDisabledMessage(t('정책상 출금이 불가합니다.'));
        }
      } else {
        console.log('출금 설정이 없음');
        setSelectedCompanyId('');
        setWithdrawDisabledMessage(t('출금 설정이 없습니다.'));
      }
      
      // 금액 리셋
      setWithdrawAmount('');
      calculateFee('');
    }
  }, [selectedAssetType]);
  
  // 금액이 변경될 때마다 수수료 계산
  useEffect(() => {
    calculateFee(withdrawAmount);
  }, [withdrawAmount, selectedCompanyId]);
  
  // 수수료 계산 함수
  const calculateFee = (amount) => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setFeeAmount(0);
      setReceiveAmount(0);
      return;
    }
    
    const numAmount = Number(amount);
    const assetInfo = getSelectedAssetTypeInfo();
    
    if (!assetInfo || !assetInfo.withdrawal || !assetInfo.withdrawal.companySettings) {
      console.log('수수료 계산 불가: 출금 설정 없음');
      setFeeAmount(0);
      setReceiveAmount(numAmount);
      return;
    }
    
    // 선택된 회사 설정 찾기
    const companySettings = assetInfo.withdrawal.companySettings[0];
    
    console.log('수수료 계산을 위한 회사 설정:', {
      selectedCompanyId,
      companySettings,
      feeInfo: companySettings?.withdrawalSettings
    });
    
    if (!companySettings || !companySettings.withdrawalSettings) {
      console.log('수수료 계산 불가: 회사 설정 또는 수수료 정보 없음');
      setFeeAmount(0);
      setReceiveAmount(numAmount);
      return;
    }
    
    // 수수료 계산
    let fee = 0;
    console.log('수수료 계산 회사 설정:', companySettings?.withdrawalSettings);
    if (companySettings?.withdrawalSettings.feeType === 'FIXED') {
      fee = Number(companySettings?.withdrawalSettings.fee);
      console.log(`고정 수수료 계산: ${fee}`);
    } else if (companySettings?.withdrawalSettings.feeType === 'PERCENT') {
      fee = numAmount * (companySettings?.withdrawalSettings.feePercent / 100);
      console.log(`퍼센트 수수료 계산: ${numAmount} * ${companySettings?.withdrawalSettings.feePercent}% = ${fee}`);
    }
    
    setFeeAmount(fee);
    setReceiveAmount(numAmount - fee);
  };
  
  // 출금 가능 여부 확인
  const isWithdrawalEnabled = (assetInfo) => {
    if (!assetInfo || !assetInfo.withdrawal) return false;
    
    // 기본 출금 활성화 여부
    if (!assetInfo.withdrawal.enabled) return false;
    
    // 선택된 회사 설정 확인
    if (selectedCompanyId && assetInfo.withdrawal.companySettings) {
      const companySettings = assetInfo.withdrawal.companySettings.find(
        company => company.companyId.toString() === selectedCompanyId
      );
      
      if (companySettings && !companySettings.enabled) {
        return false;
      }
    }
    
    return true;
  };
  
  // 출금 가능 여부에 대한 메시지 생성
  const getWithdrawalDisabledMessage = (assetInfo) => {
    // 디버깅 로그
    console.log('출금 불가 메시지 생성용 자산 정보:', {
      assetName: assetInfo?.assetName,
      withdrawal: assetInfo?.withdrawal,
      companyId: selectedCompanyId
    });
    
    if (!assetInfo || !assetInfo.withdrawal) {
      return t('출금 설정이 없습니다.');
    }
    
    if (!assetInfo.withdrawal.enabled) {
      return t('이 자산은 현재 출금이 비활성화되어 있습니다.');
    }
    
    // 선택된 회사 설정 확인
    if (selectedCompanyId && assetInfo.withdrawal.companySettings) {
      const companySettings = assetInfo.withdrawal.companySettings.find(
        company => company.companyId.toString() === selectedCompanyId
      );
      
      if (companySettings && !companySettings.withdrawalEnabled) {
        return t(`${companySettings.companyName}에서 현재 출금이 비활성화되어 있습니다.`);
      }
    }
    
    const availableAmount = assetInfo.actualAvailableAmount || assetInfo.availableAmount || 0;
    if (availableAmount <= 0) {
      return t('출금 가능한 금액이 없습니다.');
    }
    
    return '';
  };
  
  // 회사 선택 핸들러 (내부 로직용으로 유지, UI에서는 사용하지 않음)
  const handleCompanyChange = (newCompanyId) => {
    console.log('회사 변경:', { 
      oldCompanyId: selectedCompanyId, 
      newCompanyId: newCompanyId 
    });
    
    setSelectedCompanyId(newCompanyId);
    
    // 회사 변경 시 출금 가능 여부 검사
    const assetInfo = getSelectedAssetTypeInfo();
    if (assetInfo && assetInfo.withdrawal && assetInfo.withdrawal.companySettings) {
      const companySettings = assetInfo.withdrawal.companySettings.find(
        company => company.companyId.toString() === newCompanyId
      );
      
      console.log('선택된 회사 설정:', companySettings);
      
      if (companySettings && !companySettings.withdrawalEnabled) {
        setWithdrawDisabledMessage(t(`${companySettings.companyName}에서 현재 출금이 비활성화되어 있습니다.`));
      } else if (companySettings) {
        setWithdrawDisabledMessage('');
      } else {
        setWithdrawDisabledMessage(t('회사 설정을 찾을 수 없습니다.'));
      }
    }
    
    // 수수료 재계산
    calculateFee(withdrawAmount);
  };
  
  // 출금 금액 입력 처리
  const handleWithdrawAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 입력 가능
    setWithdrawAmount(value);
  };
  
  // 출금 요청 폼 렌더링
  const renderWithdrawForm = () => {
    // 선택된 자산 정보 가져오기
    const assetInfo = getSelectedAssetTypeInfo();
    const displayName = assetInfo.displayName || t('원');
    const availableAmount = assetInfo.actualAvailableAmount || assetInfo.availableAmount || 0;
    
    // 출금 가능 여부 확인
    const withdrawEnabled = isWithdrawalEnabled(assetInfo);
    const disabledMessage = withdrawDisabledMessage || getWithdrawalDisabledMessage(assetInfo);
    
    // 최소/최대 출금 금액 확인
    let minWithdrawAmount = 0;
    let maxWithdrawAmount = 0;
    let companyName = '';
    
    if (selectedCompanyId && assetInfo && assetInfo.withdrawal && assetInfo.withdrawal.companySettings) {
      const companySettings = assetInfo.withdrawal.companySettings.find(
        company => company.companyId.toString() === selectedCompanyId
      );
      
      if (companySettings && companySettings.limits) {
        minWithdrawAmount = companySettings.limits.min;
        maxWithdrawAmount = Math.min(companySettings.limits.max, availableAmount);
        companyName = companySettings.companyName;
      }
    }
    
    return (
      <div className="withdraw-form-container">
        <h3>{t("자산 출금 요청")}</h3>
        <form className="withdraw-form" onSubmit={handleWithdrawRequest}>
          {/* 자산 유형 선택기 */}
          <div className="form-group">
            <label htmlFor="withdraw-asset-type-select">{t("자산 유형")}</label>
            <div className="custom-select-wrapper">
              <select 
                id="withdraw-asset-type-select" 
                value={selectedAssetType} 
                onChange={handleAssetTypeChange}
                disabled={withdrawProcessing || assetTypes.length <= 1} // 하나만 있으면 선택 비활성화
                className="custom-select"
              >
                {assetTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name} {type.displayName && type.displayName !== type.name ? `(${type.displayName})` : ''}
                  </option>
                ))}
              </select>
              <div className="select-arrow">
                <ArrowDownIcon />
              </div>
            </div>
            <div className="point-available">
              {t("사용 가능한 자산")}: {availableAmount.toLocaleString()} {displayName}
            </div>
            {assetInfo.pendingWithdrawals > 0 && (
              <div className="point-pending">
                {t("출금 대기 중")}: {assetInfo.pendingWithdrawals.toLocaleString()} {displayName}
              </div>
            )}
            <div className="point-available">
              {t("전체 잔액")}: {assetInfo.amount.toLocaleString()} {displayName}
            </div>
          </div>
          
          {/* 회사 선택기는 UI에서 제거하고 내부적으로만 처리 */}
          
          <div className="form-group">
            <label>{t("출금 금액")}</label>
            <input
              type="text"
              value={withdrawAmount}
              onChange={handleWithdrawAmountChange}
              placeholder={t("출금할 금액을 입력하세요")}
              disabled={withdrawProcessing || !withdrawEnabled}
              required
              className="input-styled"
            />
            {minWithdrawAmount > 0 && (
              <div className="withdraw-limit-info">
                {t("최소 출금액")}: {minWithdrawAmount.toLocaleString()} {displayName}
              </div>
            )}
            {maxWithdrawAmount > 0 && (
              <div className="withdraw-limit-info">
                {t("최대 출금액")}: {maxWithdrawAmount.toLocaleString()} {displayName}
              </div>
            )}
            
            {/* 수수료 및 실제 받을 금액 표시 */}
            {withdrawAmount && Number(withdrawAmount) > 0 && (
              <div className="fee-calculation">
                <div className="fee-info">
                  {t("수수료")}: {feeAmount.toLocaleString()} {displayName}
                </div>
                <div className="receive-info">
                  {t("실제 받을 금액")}: {receiveAmount.toLocaleString()} {displayName}
                </div>
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label>{t("출금 주소")}</label>
            <input
              type="text"
              value={withdrawToAddress}
              onChange={(e) => setWithdrawToAddress(e.target.value)}
              placeholder={t("출금할 주소를 입력하세요")}
              disabled={withdrawProcessing || !withdrawEnabled}
              required
              className="input-styled"
            />
            <div className="address-helper">
              {t("선택한 자산의 올바른 주소 형식을 입력해주세요.")}
            </div>
          </div>
          
          <div className="form-group">
            <label>{t("메모 (선택사항)")}</label>
            <input
              type="text"
              value={withdrawMemo}
              onChange={(e) => setWithdrawMemo(e.target.value)}
              placeholder={t("메모를 입력하세요")}
              disabled={withdrawProcessing || !withdrawEnabled}
              className="input-styled"
            />
          </div>
          
          {!withdrawEnabled && disabledMessage && (
            <div className="withdrawal-disabled-message">
              {disabledMessage}
            </div>
          )}
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn"
              onClick={toggleWithdrawForm}
              disabled={withdrawProcessing}
            >
              {t("취소")}
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={
                withdrawProcessing || 
                !withdrawEnabled || 
                availableAmount <= 0 ||
                (minWithdrawAmount > 0 && Number(withdrawAmount) < minWithdrawAmount) ||
                (maxWithdrawAmount > 0 && Number(withdrawAmount) > maxWithdrawAmount)
              }
            >
              {withdrawProcessing ? t('처리 중...') : t('출금 요청')}
            </button>
          </div>
        </form>
      </div>
    );
  };
  
  // 사용자 보유 자산 목록 조회 함수
  const loadUserAssetBalances = async () => {
    try {
      // 로그인 상태 확인
      if (!isUserLoggedIn) {
        console.error('사용자가 로그인되어 있지 않습니다.');
        return;
      }
      
      setIsLoadingBalances(true);
      
      // API 엔드포인트
      const apiEndpoint = `/api/assets/user/balances`;
      
      // 세션 토큰 또는 로그인 쿠키 가져오기
      const userToken = localStorage.getItem('userToken') || '';
      
      // 헤더 설정 - Authorization은 토큰이 있을 때만 추가
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // 토큰이 있는 경우에만 Authorization 헤더 추가
      if (userToken) {
        headers['Authorization'] = `Bearer ${userToken}`;
      }
      
      // fetch API 직접 호출
      const response = await fetch(apiEndpoint, {
        method: 'GET',
        headers: headers,
        credentials: 'include', // 쿠키 포함
      });
      
      if (!response.ok) {
        // 401 Unauthorized 응답 처리
        if (response.status === 401) {
          console.error('인증되지 않은 요청입니다. 다시 로그인해주세요.');
          return;
        }
        throw new Error(`API 요청 실패: ${response.status}`);
      }
      
      const responseData = await response.json();
      
      if (responseData.statusCode !== 200) {
        throw new Error(`API 응답 오류: ${responseData.message}`);
      }
      
      // 데이터 설정 - 응답 구조가 변경됨
      const balances = responseData.assetBalances || [];
      
      // 디버깅 로그 추가
      console.log('API에서 받은 자산 데이터:', balances);
      console.log('wallet 정보 있는 자산:', balances.filter(asset => asset.wallet && asset.wallet.address));
      
      setUserAssetBalances(balances);
      
      // 자산 유형 정보 생성
      const assetTypeList = generateAssetTypeList(balances);
      setAssetTypes(assetTypeList);
      
      // 첫 번째 자산 유형을 자동 선택 (빈 배열이 아닐 경우)
      if (assetTypeList.length > 0 && selectedAssetType === '') {
        setSelectedAssetType(assetTypeList[0].id.toString());
      }
      
      console.log('사용자 보유 자산 목록:', balances);
    } catch (err) {
      console.error('사용자 보유 자산 목록 조회 중 오류:', err);
    } finally {
      setIsLoadingBalances(false);
    }
  };
  
  // 자산 유형 목록 생성 함수
  const generateAssetTypeList = (balances) => {
    // 고유한 자산 유형 맵 생성
    const assetTypeMap = new Map();
    
    // 사용자 보유 자산 목록에서 자산 유형 정보 추출
    balances.forEach(balance => {
      if (balance.assetTypeId && !assetTypeMap.has(balance.assetTypeId.toString())) {
        // 회사별 표시 이름 가져오기 (첫 번째 회사 정보 사용)
        const companyIds = Object.keys(balance.companyDisplayNames || {});
        const displayInfo = companyIds.length > 0 
          ? balance.companyDisplayNames[companyIds[0]] 
          : { displayName: balance.assetName };
        
        assetTypeMap.set(balance.assetTypeId.toString(), {
          id: balance.assetTypeId,
          name: balance.assetName,
          code: balance.assetCode,
          displayName: displayInfo.displayName || balance.assetName,
          iconUrl: balance.iconUrl,
          amount: balance.amount,
          availableAmount: balance.availableAmount
        });
      }
    });
    
    // Map을 배열로 변환
    return Array.from(assetTypeMap.values());
  };
  
  // 선택한 자산 유형에 대한 정보 가져오기
  const getSelectedAssetTypeInfo = () => {
    // 선택한 자산 유형의 정보 찾기
    const selectedAsset = userAssetBalances.find(
      balance => balance.assetTypeId.toString() === selectedAssetType.toString()
    );
    
    
    if (!selectedAsset) {
      return { displayName: t('원'), amount: 0, availableAmount: 0 };
    }
    
    // 회사별 표시 이름 가져오기 (첫 번째 회사 정보 사용)
    const companyIds = Object.keys(selectedAsset.companyDisplayNames || {});
    const displayInfo = companyIds.length > 0 
      ? selectedAsset.companyDisplayNames[companyIds[0]] 
      : { displayName: selectedAsset.assetName };
    
    // 원본 자산 객체 그대로 반환 (withdrawal 속성 포함)
    return {
      ...selectedAsset,
      displayName: displayInfo.displayName || selectedAsset.assetName
    };
  };
  
  // 자산 내역 로드 함수
  const loadAssetHistory = async (pageNum) => {
    try {
      // 로그인 상태 확인
      if (!isUserLoggedIn) {
        console.error('사용자가 로그인되어 있지 않습니다.');
        setApiError(t('로그인이 필요한 서비스입니다.'));
        return;
      }

      setIsLoading(true);
      
      // API 엔드포인트
      const apiEndpoint = `/api/asset-transactions/my?page=${pageNum}&limit=10`;
      
      // 세션 토큰 또는 로그인 쿠키 가져오기
      const userToken = localStorage.getItem('userToken') || '';
      
      // 헤더 설정 - Authorization은 토큰이 있을 때만 추가
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // 토큰이 있는 경우에만 Authorization 헤더 추가
      if (userToken) {
        headers['Authorization'] = `Bearer ${userToken}`;
      }
      
      // fetch API 직접 호출
      const response = await fetch(apiEndpoint, {
        method: 'GET',
        headers: headers,
        credentials: 'include', // 쿠키 포함
      });
      
      if (!response.ok) {
        // 401 Unauthorized 응답 처리
        if (response.status === 401) {
          console.error('인증되지 않은 요청입니다. 다시 로그인해주세요.');
          setApiError(t('세션이 만료되었습니다. 다시 로그인해주세요.'));
          return;
        }
        throw new Error(`API 요청 실패: ${response.status}`);
      }
      
      const data = await response.json();
      
      // 데이터 설정
      const transactions = data.items || [];
      setAssetData(transactions);
      
      // 자산 유형 추출
      const uniqueAssetTypes = extractUniqueAssetTypes(transactions);
      setAssetTypes(uniqueAssetTypes);
      
      // 사용자가 가진 출금 가능한 자산의 잔액 계산
      // 실제 API에서 이 값을 제공하는 경우 해당 값 사용
      const availableBalance = data.availableBalance !== undefined ? 
        data.availableBalance : 
        transactions.reduce((sum, tx) => sum + tx.amount, 0);
      setTotalSum(availableBalance);
      
      // 총 수급액 계산 (양수 금액만 합산)
      // 실제 API에서 이 값을 제공하는 경우 해당 값 사용
      const totalReceived = data.totalReceived !== undefined ?
        data.totalReceived :
        transactions.filter(tx => tx.amount > 0).reduce((sum, tx) => sum + tx.amount, 0);
      setTotalGotMileage(totalReceived);
      
      // 총 항목 수 설정
      setTotalCount(data.meta?.total || 0);
      
      // 데이터가 없는 페이지인 경우 이전 페이지로 이동
      if (transactions.length === 0 && pageNum > 1) {
        setPage(pageNum - 1);
      }
      
      setApiError(null);
    } catch (err) {
      console.error('자산 내역 로드 중 오류:', err);
      setApiError(t('자산 내역을 불러오는 중 오류가 발생했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // 고유한 자산 유형 추출 함수
  const extractUniqueAssetTypes = (transactions) => {
    // 고유한 자산 유형 맵 생성
    const assetTypeMap = new Map();
    
    // 트랜잭션에서 고유한 자산 유형 추출
    transactions.forEach(tx => {
      if (tx.assetType && tx.assetType.id && !assetTypeMap.has(tx.assetType.id)) {
        assetTypeMap.set(tx.assetType.id, {
          id: tx.assetType.id,
          name: tx.assetType.name || t('자산'),
          code: tx.assetType.code || '',
          displayName: tx.companyAssetType?.displayName || t('원'),
        });
      }
    });
    
    // Map을 배열로 변환
    return Array.from(assetTypeMap.values());
  };
  
  // 자산 유형 변경 핸들러
  const handleAssetTypeChange = (e) => {
    setSelectedAssetType(e.target.value);
  };
  
  // 선택된 자산 유형에 따라 자산 데이터 필터링
  const getFilteredAssetData = () => {
    // 모든 자산 내역 반환 (필터링 없음)
    return assetData || [];
  };
  
  // 자산 데이터
  const filteredAssetData = getFilteredAssetData();
  
  // 현재 선택된 자산 유형의 정보
  const selectedAssetInfo = useMemo(() => {
    return getSelectedAssetTypeInfo();
  }, [selectedAssetType, userAssetBalances]);
  
  // 현재 선택한 자산의 출금 가능 여부
  const isSelectedAssetWithdrawable = useMemo(() => {
    const assetInfo = selectedAssetInfo;
    
    
    // 기본 출금 조건 확인
    if (!assetInfo || !assetInfo.withdrawal) {
      return false;
    }
    
    // 출금 활성화 여부 확인
    if (!assetInfo.withdrawal.enabled) {
      return false;
    }
    
    // 사용 가능 금액 확인
    const availableAmount = assetInfo.actualAvailableAmount || assetInfo.availableAmount || 0;
    if (availableAmount <= 0) {
      return false;
    }
    
    // 출금 가능한 회사 있는지 확인
    if (assetInfo.withdrawal.companySettings && assetInfo.withdrawal.companySettings.length > 0) {
      return assetInfo.withdrawal.companySettings.some(company => company.enabled);
    }
    
    return false;
  }, [selectedAssetInfo]);
  
  // 각 자산 유형별 출금 가능 여부 확인
  const getAssetWithdrawalStatus = (assetTypeId) => {
    const assetInfo = userAssetBalances.find(
      balance => balance.assetTypeId.toString() === assetTypeId.toString()
    );
    if (!assetInfo || !assetInfo.withdrawal) {
      return { enabled: false, message: t('출금 설정이 없습니다.') };
    }
    if (!assetInfo.withdrawal.enabled) {
      return { enabled: false, message: t('출금이 비활성화되어 있습니다.') };
    }
    // 금액 조건 제거됨
    // if (availableAmount <= 0) {
    //   return { enabled: false, message: t('출금 가능한 금액이 없습니다.') };
    // }
    if (assetInfo.withdrawal.companySettings && assetInfo.withdrawal.companySettings.length > 0) {
      const hasEnabledCompany = assetInfo.withdrawal.companySettings.some(company => company.withdrawalEnabled);
      if (!hasEnabledCompany) {
        return { enabled: false, message: t('출금 가능한 회사가 없습니다.') };
      }
      return { enabled: true, message: '' };
    }
    return { enabled: false, message: t('출금 설정이 없습니다.') };
  };
  
  // 자산 아이콘 컴포넌트
  const AssetIcon = ({ iconUrl, assetName }) => {
    if (!iconUrl) {
      // 기본 아이콘
      return <PointIcon />;
    }
    
    // 이미지 아이콘
    return (
      <img 
        src={iconUrl} 
        alt={assetName || t('자산')} 
        className="asset-icon" 
        width="16" 
        height="16" 
      />
    );
  };
  
  // 필터링된 자산의 표시 단위 결정
  const assetDisplayUnit = useMemo(() => {
    return selectedAssetInfo.displayName || t('원');
  }, [selectedAssetInfo]);
  
  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  // 날짜 포맷 함수
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    
    // 날짜 형식
    const dateStr = `${date.getFullYear()}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getDate().toString().padStart(2, '0')}`;
    
    return dateStr;
  };
  
  // 금액 포맷 함수
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return '0';
    
    // 양수인 경우 + 기호 추가, 음수는 - 기호가 이미 포함됨
    const sign = amount > 0 ? '+' : '';
    
    return `${sign}${Number(amount).toLocaleString()}`;
  };
  
  // 출금 요청 폼 토글
  const toggleWithdrawForm = () => {
    setShowWithdrawForm(!showWithdrawForm);
  };
  
  // 출금 요청 처리
  const handleWithdrawRequest = async (e) => {
    e.preventDefault();
    
    // 로그인 상태 확인
    if (!isUserLoggedIn) {
      alert(t('로그인이 필요한 서비스입니다.'));
      return;
    }
    
    // 선택된 자산 유형 정보 가져오기
    const assetInfo = getSelectedAssetTypeInfo();
    
    // 출금 가능 여부 확인
    if (!isWithdrawalEnabled(assetInfo)) {
      alert(withdrawDisabledMessage || getWithdrawalDisabledMessage(assetInfo) || t('출금할 수 없는 자산입니다.'));
      return;
    }
    
    // 입력값 검증
    if (!withdrawAmount || isNaN(Number(withdrawAmount)) || Number(withdrawAmount) <= 0) {
      alert(t('유효한 출금 금액을 입력해주세요.'));
      return;
    }
    
    // 최소/최대 출금 금액 확인
    let minWithdrawAmount = 0;
    let maxWithdrawAmount = 0;
    
    if (selectedCompanyId && assetInfo && assetInfo.withdrawal && assetInfo.withdrawal.companySettings) {
      const companySettings = assetInfo.withdrawal.companySettings.find(
        company => company.companyId.toString() === selectedCompanyId
      );
      
      if (companySettings && companySettings.limits) {
        minWithdrawAmount = companySettings.limits.min;
        maxWithdrawAmount = Math.min(companySettings.limits.max, assetInfo.actualAvailableAmount || assetInfo.availableAmount);
      }
    }
    
    // 최소 금액 검증
    if (minWithdrawAmount > 0 && Number(withdrawAmount) < minWithdrawAmount) {
      alert(t(`최소 출금 금액은 ${minWithdrawAmount.toLocaleString()} ${assetInfo.displayName}입니다.`));
      return;
    }
    
    // 최대 금액 검증
    if (maxWithdrawAmount > 0 && Number(withdrawAmount) > maxWithdrawAmount) {
      alert(t(`최대 출금 금액은 ${maxWithdrawAmount.toLocaleString()} ${assetInfo.displayName}입니다.`));
      return;
    }
    
    // 사용 가능 금액 검증
    const availableAmount = assetInfo.actualAvailableAmount || assetInfo.availableAmount || 0;
    if (Number(withdrawAmount) > availableAmount) {
      alert(t('보유 자산보다 많은 금액을 출금할 수 없습니다.'));
      return;
    }
    
    if (!withdrawToAddress) {
      alert(t('출금 주소를 입력해주세요.'));
      return;
    }
    
    try {
      setWithdrawProcessing(true);
      
      // API 엔드포인트
      const apiEndpoint = '/api/asset-transactions/withdraw';
      
      // 세션 토큰 또는 로그인 쿠키 가져오기
      const userToken = localStorage.getItem('userToken') || '';
      
      // 요청 데이터 - 명시적으로 숫자 타입으로 변환
      const requestData = {
        assetTypeId: Number(selectedAssetType),
        companyId: Number(selectedCompanyId),
        amount: Number(withdrawAmount),
        toAddress: withdrawToAddress,
        desc: withdrawMemo || t('자산 출금 요청')
      };
      
      console.log('출금 요청 데이터:', requestData);
      
      // 헤더 설정 - Authorization은 토큰이 있을 때만 추가
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // 토큰이 있는 경우에만 Authorization 헤더 추가
      if (userToken) {
        headers['Authorization'] = `Bearer ${userToken}`;
      }
      
      // API 호출
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify(requestData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // 401 Unauthorized 응답 처리
        if (response.status === 401) {
          throw new Error(t('세션이 만료되었습니다. 다시 로그인해주세요.'));
        }
        // 다른 오류 응답 처리
        throw new Error(data.message || data.error || `${t('출금 요청 실패')}: ${response.status}`);
      }
      
      // 성공 메시지 표시
      alert(t(`출금 요청이 접수되었습니다. 수수료 `)+`${feeAmount.toLocaleString()} ${assetInfo.displayName} `+t('차감된')+` ${receiveAmount.toLocaleString()} ${assetInfo.displayName} `+t('가 처리됩니다.'));
      
      // 폼 초기화 및 숨기기
      setWithdrawAmount('');
      setWithdrawToAddress('');
      setWithdrawMemo('');
      setShowWithdrawForm(false);
      
      // 내역 새로고침
      loadUserAssetBalances();
      loadAssetHistory(1);
      setPage(1);
    } catch (err) {
      console.error('출금 요청 중 오류:', err);
      alert(`${t('출금 요청 중 오류가 발생했습니다')}: ${err.message}`);
    } finally {
      setWithdrawProcessing(false);
    }
  };
  
  // 환전 가능한 자산인지 확인
  const isAssetExchangeable = (assetInfo) => {
    if (!assetInfo || !assetInfo.exchange) return false;
    
    // 기본 환전 활성화 여부
    if (!assetInfo.exchange.enabled) return false;
    
    // 사용 가능 금액 확인
    const availableAmount = assetInfo.actualAvailableAmount || assetInfo.availableAmount || 0;
    if (availableAmount <= 0) return false;
    
    // 회사별 환전 설정 확인
    if (assetInfo.exchange.companySettings && assetInfo.exchange.companySettings.length > 0) {
      // 선택된 회사만 확인, 없으면 첫 번째 회사 사용
      const companyIds = Object.keys(assetInfo.companyDisplayNames || {});
      const companyId = selectedCompanyId || (companyIds.length > 0 ? companyIds[0] : null);
      
      if (companyId) {
        const companySettings = assetInfo.exchange.companySettings.find(
          company => company.companyId.toString() === companyId
        );
        
        if (companySettings) {
          return companySettings.exchangeEnabled && companySettings.exchangeFromEnabled;
        }
      }
      
      // 선택된 회사가 없으면 하나라도 환전 가능한 회사가 있는지 확인
      return assetInfo.exchange.companySettings.some(
        company => company.exchangeEnabled && company.exchangeFromEnabled
      );
    }
    
    return true;
  };

  // 환전 불가 메시지 생성
  const getExchangeDisabledMessage = (assetInfo) => {
    if (!assetInfo || !assetInfo.exchange) {
      return t('환전 설정이 없습니다.');
    }
    
    if (!assetInfo.exchange.enabled) {
      return t('이 자산은 현재 환전이 비활성화되어 있습니다.');
    }
    
    const availableAmount = assetInfo.actualAvailableAmount || assetInfo.availableAmount || 0;
    if (availableAmount <= 0) {
      return t('환전 가능한 금액이 없습니다.');
    }
    
    // 회사별 환전 설정 확인
    if (assetInfo.exchange.companySettings && assetInfo.exchange.companySettings.length > 0) {
      const companyIds = Object.keys(assetInfo.companyDisplayNames || {});
      const companyId = selectedCompanyId || (companyIds.length > 0 ? companyIds[0] : null);
      
      if (companyId) {
        const companySettings = assetInfo.exchange.companySettings.find(
          company => company.companyId.toString() === companyId
        );
        
        if (companySettings) {
          if (!companySettings.exchangeEnabled) {
            return t(`${companySettings.companyName}에서 현재 환전이 비활성화되어 있습니다.`);
          }
          
          if (!companySettings.exchangeFromEnabled) {
            return t(`${companySettings.companyName}에서 이 자산은 환전 출발 자산으로 사용할 수 없습니다.`);
          }
        }
      }
      
      // 환전 가능한 회사가 없는 경우
      const hasEnabledCompany = assetInfo.exchange.companySettings.some(
        company => company.exchangeEnabled && company.exchangeFromEnabled
      );
      
      if (!hasEnabledCompany) {
        return t('환전 가능한 회사가 없습니다.');
      }
    }
    
    // 환전 가능한 대상 자산 여부 확인
    const targetAssets = getExchangeableTargetAssets(assetInfo.assetTypeId);
    if (targetAssets.length === 0) {
      return t('환전 가능한 대상 자산이 없습니다.');
    }
    
    return '';
  };

  // 환전 가능한 대상 자산 목록 가져오기
  const getExchangeableTargetAssets = (sourceAssetId) => {
    // 환전 가능한 모든 자산(sourceAssetId와 다른 자산)을 반환
    // companySettings에서 exchangeEnabled 및 exchangeToEnabled가 true인 자산만 대상 자산으로 선택 가능
    return userAssetBalances.filter(asset => {
      if (asset.assetTypeId === parseInt(sourceAssetId) || !asset.exchange || !asset.exchange.enabled) {
        return false;
      }
      
      // 회사별 환전 설정 확인
      if (asset.exchange.companySettings && asset.exchange.companySettings.length > 0) {
        // 선택된 회사가 있으면 해당 회사만 확인, 없으면 첫 번째 회사 사용
        const companyIds = Object.keys(asset.companyDisplayNames || {});
        const companyId = selectedCompanyId || (companyIds.length > 0 ? companyIds[0] : null);
        
        if (companyId) {
          const companySettings = asset.exchange.companySettings.find(
            company => company.companyId.toString() === companyId
          );
          
          if (companySettings) {
            return companySettings.exchangeEnabled && companySettings.exchangeToEnabled;
          }
        }
        
        // 선택된 회사가 없으면 하나라도 환전 가능한 회사가 있는지 확인
        return asset.exchange.companySettings.some(
          company => company.exchangeEnabled && company.exchangeToEnabled
        );
      }
      
      return true;
    });
  };

  // 환전 모달 열기
  const openExchangeModal = (assetInfo) => {
    console.log('환전 모달 열기:', assetInfo);
    
    // 환전 모달 초기화
    setExchangeFromAsset(assetInfo);
    setExchangeToAsset(null);
    setExchangeAmount('');
    setExchangeReason('');
    setExchangeResult(null);
    setExchangeFeeAmount(0);
    setExchangeReceiveAmount(0);
    setExchangeDisabledMessage('');
    
    // 환전 가능한 대상 자산 확인
    const targetAssets = getExchangeableTargetAssets(assetInfo.assetTypeId);
    
    if (targetAssets.length === 0) {
      setExchangeDisabledMessage(t('환전 가능한 대상 자산이 없습니다.'));
    } else if (targetAssets.length === 1) {
      // 대상 자산이 하나만 있으면 자동 선택
      setExchangeToAsset(targetAssets[0]);
    }
    
    // 모달 표시
    setShowExchangeModal(true);
  };

  // 환전 모달 닫기
  const closeExchangeModal = () => {
    setShowExchangeModal(false);
  };

  // 입금 모달 열기
  const openDepositModal = (assetInfo) => {
    console.log('입금 모달 열기:', assetInfo);
    setDepositAsset(assetInfo);
    setShowDepositModal(true);
  };

  // 입금 모달 닫기
  const closeDepositModal = () => {
    setShowDepositModal(false);
    setDepositAsset(null);
  };
  
  // 입금 지갑 주소 복사 기능
  const copyWalletAddress = (address) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(address)
        .then(() => {
          alert(t('지갑 주소가 클립보드에 복사되었습니다.'));
        })
        .catch(err => {
          console.error('주소 복사 중 오류 발생:', err);
          alert(t('지갑 주소 복사에 실패했습니다.'));
        });
    } else {
      // 클립보드 API를 지원하지 않는 경우 fallback
      const textArea = document.createElement('textarea');
      textArea.value = address;
      textArea.style.position = 'fixed';  // 화면에 보이지 않게
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          alert(t('지갑 주소가 클립보드에 복사되었습니다.'));
        } else {
          alert(t('지갑 주소 복사에 실패했습니다.'));
        }
      } catch (err) {
        console.error('주소 복사 중 오류 발생:', err);
        alert(t('지갑 주소 복사에 실패했습니다.'));
      }
      
      document.body.removeChild(textArea);
    }
  };

  // 환전 대상 자산 변경 처리
  const handleExchangeToAssetChange = (e) => {
    const targetAssetId = e.target.value;
    
    if (!targetAssetId) {
      setExchangeToAsset(null);
      return;
    }
    
    const targetAsset = userAssetBalances.find(
      asset => asset.assetTypeId.toString() === targetAssetId
    );
    
    setExchangeToAsset(targetAsset);
    
    // 금액 재계산
    calculateExchangeRate(exchangeAmount, exchangeFromAsset, targetAsset);
  };

  // 환전 금액 입력 처리
  const handleExchangeAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 입력 가능
    setExchangeAmount(value);
    
    // 환전율 계산
    calculateExchangeRate(value, exchangeFromAsset, exchangeToAsset);
  };

  // 환전율 계산
  const calculateExchangeRate = (amount, fromAsset, toAsset) => {
    if (!amount || !fromAsset || !toAsset || isNaN(Number(amount)) || Number(amount) <= 0) {
      setExchangeFeeAmount(0);
      setExchangeReceiveAmount(0);
      return;
    }
    
    const numAmount = Number(amount);
    
    // 환전 설정 확인
    if (!fromAsset.exchange || !toAsset.exchange) {
      console.log('환전율 계산 불가: 환전 설정 없음');
      setExchangeFeeAmount(0);
      setExchangeReceiveAmount(0);
      return;
    }
    
    console.log('환전율 계산을 위한 설정:', {
      fromAsset: fromAsset.assetName,
      toAsset: toAsset.assetName,
      fromExchange: fromAsset.exchange,
      toExchange: toAsset.exchange
    });
    
    // 수수료 계산 - 회사별 설정에서 fee 정보 사용
    let fee = 0;
    
    // 회사 ID 가져오기
    const companyIds = Object.keys(fromAsset.companyDisplayNames || {});
    const companyId = selectedCompanyId || (companyIds.length > 0 ? companyIds[0] : null);
    
    // 해당 회사의 환전 설정 찾기
    const companySettings = fromAsset.exchange.companySettings && 
      fromAsset.exchange.companySettings.find(cs => cs.companyId.toString() === companyId);
    
    if (companySettings && companySettings.exchangeSettings) {
      const feeSettings = companySettings.exchangeSettings;
      
      if (feeSettings.feeType === 'FIXED') {
        fee = Number(feeSettings.fee || 0);
        console.log(`고정 수수료 계산: ${fee}`);
      } else if (feeSettings.feeType === 'PERCENT') {
        fee = numAmount * (Number(feeSettings.feePercent || 0) / 100);
        console.log(`퍼센트 수수료 계산: ${numAmount} * ${feeSettings.feePercent}% = ${fee}`);
      }
    }
    
    // 수수료 차감 후 금액
    const afterFeeAmount = numAmount - fee;
    
    // 환율 적용
    const fromValue = companySettings?.exchangeSettings?.value || 1;
    const toAssetCompanySettings = toAsset.exchange.companySettings &&
      toAsset.exchange.companySettings.find(cs => cs.companyId.toString() === companyId);
    const toValue = toAssetCompanySettings?.exchangeSettings?.value || 1;
    const exchangeRate = fromValue / toValue;
    
    // 최종 환전 금액 계산
    const finalAmount = afterFeeAmount * exchangeRate;
    
    console.log('환전 금액 계산:', {
      원금: numAmount,
      수수료: fee,
      수수료차감후: afterFeeAmount,
      환율: `${fromValue}/${toValue} = ${exchangeRate}`,
      최종금액: finalAmount
    });
    
    setExchangeFeeAmount(fee);
    setExchangeReceiveAmount(finalAmount);
  };

  // 환전 요청 처리
  const handleExchangeRequest = async (e) => {
    e.preventDefault();
    
    // 로그인 상태 확인
    if (!isUserLoggedIn) {
      alert(t('로그인이 필요한 서비스입니다.'));
      return;
    }
    
    // 환전 가능 여부 확인
    if (!isAssetExchangeable(exchangeFromAsset)) {
      alert(exchangeDisabledMessage || getExchangeDisabledMessage(exchangeFromAsset) || t('환전할 수 없는 자산입니다.'));
      return;
    }
    
    if (!exchangeToAsset) {
      alert(t('환전 대상 자산을 선택해주세요.'));
      return;
    }
    
    // 입력값 검증
    if (!exchangeAmount || isNaN(Number(exchangeAmount)) || Number(exchangeAmount) <= 0) {
      alert(t('유효한 환전 금액을 입력해주세요.'));
      return;
    }
    
    // 사용 가능 금액 검증
    const availableAmount = exchangeFromAsset.actualAvailableAmount || exchangeFromAsset.availableAmount || 0;
    const minRest = exchangeFromAsset.exchange?.withdrawMinRest || 0;
    
    if (Number(exchangeAmount) > availableAmount) {
      alert(t('보유 자산보다 많은 금액을 환전할 수 없습니다.'));
      return;
    }
    
    // 최소 잔액 체크
    if ((availableAmount - Number(exchangeAmount)) < minRest) {
      alert(t(`최소 ${minRest.toLocaleString()} ${exchangeFromAsset.displayName}의 잔액을 유지해야 합니다.`));
      return;
    }
    
    try {
      setExchangeProcessing(true);
      
      // API 엔드포인트
      const apiEndpoint = '/api/assets/user/exchange';
      
      // 세션 토큰 또는 로그인 쿠키 가져오기
      const userToken = localStorage.getItem('userToken') || '';
      
      // 요청 데이터 - 명시적으로 숫자 타입으로 변환
      const requestData = {
        fromAssetTypeId: Number(exchangeFromAsset.assetTypeId),
        toAssetTypeId: Number(exchangeToAsset.assetTypeId),
        amount: Number(exchangeAmount),
        reason: exchangeReason || t('자산 환전 요청')
      };
      
      console.log('환전 요청 데이터:', requestData);
      
      // 헤더 설정 - Authorization은 토큰이 있을 때만 추가
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // 토큰이 있는 경우에만 Authorization 헤더 추가
      if (userToken) {
        headers['Authorization'] = `Bearer ${userToken}`;
      }
      
      // API 호출
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify(requestData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // 401 Unauthorized 응답 처리
        if (response.status === 401) {
          throw new Error(t('세션이 만료되었습니다. 다시 로그인해주세요.'));
        }
        // 다른 오류 응답 처리
        throw new Error(data.message || data.error || `${t('환전 요청 실패')}: ${response.status}`);
      }
      
      // 응답 저장
      setExchangeResult(data);
      
      // API 응답에서 자산 이름과 금액 가져오기
      const exchangeData = data.exchange || {};
      
      // 자산 이름 가져오기 (API 응답이나 기존 상태에서)
      const fromAssetName = (exchangeData.fromAsset?.name) || 
                           exchangeFromAsset.assetName || 
                           exchangeFromAsset.displayName || 
                           t('자산');
                         
      const toAssetName = (exchangeData.toAsset?.name) || 
                         exchangeToAsset.assetName || 
                         exchangeToAsset.displayName || 
                         t('자산');
      
      // 자산 금액 가져오기
      const fromAmount = Math.abs(exchangeData.fromAsset?.amount || Number(exchangeAmount));
      const toAmount = exchangeData.toAsset?.amount || exchangeReceiveAmount;
      
      // 성공 메시지 표시 - API 응답에서 가져온 자산 이름과 금액 사용
      alert(t(`환전 요청이 성공적으로 처리되었습니다.`)+` ${fromAssetName} ${fromAmount.toLocaleString()}`+t('에서')+`${toAssetName} ${toAmount.toLocaleString()}`+t('로 환전되었습니다.'));
      
      // 내역 새로고침
      loadUserAssetBalances();
      loadAssetHistory(1);
      setPage(1);
      
      // 모달 닫기
      closeExchangeModal();
    } catch (err) {
      console.error('환전 요청 중 오류:', err);
      alert(`${t('환전 요청 중 오류가 발생했습니다')}: ${err.message}`);
    } finally {
      setExchangeProcessing(false);
    }
  };
  
  // 페이지네이션 컴포넌트
  const Pagination = ({ currentPage, totalItems }) => {
    // 데이터가 없으면 페이지네이션 미표시
    if (!totalItems) return null;
    
    // 한 페이지당 아이템 수 (서버에서 정의한 값과 일치해야 함)
    const ITEMS_PER_PAGE = 10;
    
    // 실제 총 페이지 수 계산
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    
    // 표시할 페이지 번호 배열
    const pageNumbers = [];
    
    // 최대로 표시할 페이지 번호 수
    const maxVisiblePages = Math.min(totalPages, 2); // 실제 페이지 수와 2 중 작은 값
    
    // 페이지 번호 계산 - 현재 페이지가 중앙에 오도록
    let startPage = Math.max(1, Math.min(currentPage, totalPages - maxVisiblePages + 1));
    const endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);
    
    // 페이지 번호 배열 생성
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    
    return (
      <div className="pagination-container">
        <button 
          className="pagination-button prev" 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {t("이전")}
        </button>
        
        {pageNumbers.map(number => (
          <button
            key={number}
            className={`pagination-button ${number === currentPage ? 'active' : ''}`}
            onClick={() => handlePageChange(number)}
          >
            {number}
          </button>
        ))}
        
        <button 
          className="pagination-button next" 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t("다음")}
        </button>
      </div>
    );
  };
  
  // 세션 정보 디버깅 함수 (개발 모드에서만 사용)
  const debugAuthInfo = () => {
    // 개발 모드에서만 실행
    if (process.env.NODE_ENV !== 'development') return;
    
    console.group('인증 디버깅 정보');
    console.log('로그인 상태:', isUserLoggedIn);
    console.log('사용자 정보:', userInfo);
    console.log('로컬스토리지 토큰:', localStorage.getItem('userToken'));
    console.log('세션스토리지 토큰:', sessionStorage.getItem('userToken'));
    
    // 쿠키 정보 출력
    console.log('쿠키:', document.cookie);
    
    // Redux 스토어 상태 출력
    const state = store?.getState?.() || {};
    console.log('Redux 스토어:', state);
    console.groupEnd();
  };
  
  // 컴포넌트 마운트 시 디버깅 정보 출력 (개발 모드)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !isPreview) {
      debugAuthInfo();
    }
  }, []);

  // 미리보기 모드인 경우 미리보기 데이터 표시
  if (isPreview) {
    // 미리보기용 자산 종류 정보
    const previewAssetTypes = [
      { 
        id: 'all', 
        name: t('전체 자산'), 
        code: 'ALL',
      },
      {
        id: 1,
        name: t('포인트'),
        code: 'POINT',
        displayName: t('위드쿠키 포인트'),
        iconUrl: 'https://example.com/icons/point.png',
        amount: 5000,
        availableAmount: 5000,
        exchange: {
          enabled: true,
          value: 1,
          withdrawMinRest: 500,
          feeType: 'PERCENT',
          feePercent: 5
        }
      },
      {
        id: 2,
        name: t('마일리지'),
        code: 'MILEAGE',
        displayName: t('위드쿠키 마일리지'),
        iconUrl: 'https://example.com/icons/mileage.png',
        amount: 2500,
        availableAmount: 2500,
        exchange: {
          enabled: true,
          value: 2,
          withdrawMinRest: 300,
          feeType: 'FIXED',
          feeAmount: 100
        }
      }
    ];
    
    // 미리보기용 자산 잔액 데이터
    const previewAssetBalances = [
      {
        assetTypeId: 1,
        assetName: t('포인트'),
        assetCode: 'POINT',
        iconUrl: 'https://example.com/icons/point.png',
        amount: 5000,
        availableAmount: 5000,
        companyDisplayNames: {
          "1": {
            displayName: t('위드쿠키 포인트'),
            companyName: t('위드쿠키')
          }
        },
        exchange: {
          enabled: true,
          value: 1,
          withdrawMinRest: 500,
          feeType: 'PERCENT',
          feePercent: 5
        },
        lastUpdatedAt: new Date().toISOString()
      },
      {
        assetTypeId: 2,
        assetName: t('마일리지'),
        assetCode: 'MILEAGE',
        iconUrl: 'https://example.com/icons/mileage.png',
        amount: 2500,
        availableAmount: 2500,
        companyDisplayNames: {
          "1": {
            displayName: t('위드쿠키 마일리지'),
            companyName: t('위드쿠키')
          }
        },
        exchange: {
          enabled: true,
          value: 2,
          withdrawMinRest: 300,
          feeType: 'FIXED',
          feeAmount: 100
        },
        lastUpdatedAt: new Date(Date.now() - 86400000).toISOString()
      }
    ];
    
    // 미리보기용 거래 내역 데이터
    const previewAssets = [
      {
        id: "20c04503-40d6-4215-9ed3-813ee33e8f7e",
        type: "CREATED_BY_COMPANY",
        amount: 3000,
        desc: t('회원가입 축하 포인트'),
        createdAt: new Date().toISOString(),
        userId: 32503,
        assetId: 1,
        assetType: {
          id: 1,
          name: t('포인트'),
          code: 'POINT'
        },
        companyAssetType: { 
          displayName: t('위드쿠키 포인트')
        }
      },
      {
        id: "30d04503-50a6-1515-8ed3-813ee44e8f7a",
        type: "ORDER_NORMAL",
        amount: -1000,
        desc: t('상품 주문 사용'),
        createdAt: new Date(Date.now() - 86400000).toISOString(), // 1일 전
        userId: 32503,
        orderId: 'ORD12345678',
        assetId: 1,
        assetType: {
          id: 1,
          name: t('포인트'),
          code: 'POINT'
        },
        companyAssetType: { 
          displayName: t('위드쿠키 포인트')
        }
      },
      {
        id: "40e04503-60b6-7515-9ed3-813ee55e8f7b",
        type: "CREATED_BY_COMPANY",
        amount: 3000,
        desc: t('시즌 포인트 지급'),
        createdAt: new Date(Date.now() - 172800000).toISOString(), // 2일 전
        userId: 32503,
        assetId: 1,
        assetType: {
          id: 1,
          name: t('포인트'),
          code: 'POINT'
        },
        companyAssetType: { 
          displayName: t('위드쿠키 포인트')
        }
      },
      {
        id: "50f04503-70c6-8515-0ed3-813ee66e8f7c",
        type: "WITHDRAWAL_REQUESTED",
        amount: -1000,
        desc: t('출금 신청'),
        status: 'PENDING',
        createdAt: new Date(Date.now() - 259200000).toISOString(), // 3일 전
        userId: 32503,
        assetId: 2,
        assetType: {
          id: 2,
          name: t('마일리지'),
          code: 'MILEAGE'
        },
        companyAssetType: { 
          displayName: t('위드쿠키 마일리지')
        }
      },
      {
        id: "60g04503-80d6-9515-1ed3-813ee77e8f7d",
        type: "CREATED_BY_SYSTEM",
        amount: 1500,
        desc: t('이벤트 참여 마일리지'),
        createdAt: new Date(Date.now() - 345600000).toISOString(), // 4일 전
        userId: 32503,
        assetId: 2,
        assetType: {
          id: 2,
          name: t('마일리지'),
          code: 'MILEAGE'
        },
        companyAssetType: { 
          displayName: t('위드쿠키 마일리지')
        }
      },
      {
        id: "70h04503-90e6-0515-2ed3-813ee88e8f7e",
        type: "MILEAGE_USED",
        amount: -1000,
        desc: t('마일리지 사용'),
        createdAt: new Date(Date.now() - 432000000).toISOString(), // 5일 전
        userId: 32503,
        assetId: 2,
        assetType: {
          id: 2,
          name: t('마일리지'),
          code: 'MILEAGE'
        },
        companyAssetType: { 
          displayName: t('위드쿠키 마일리지')
        }
      }
    ];
    
    // 미리보기 데이터 필터링
    const filteredPreviewAssets = previewSelectedAssetType === 'all' 
      ? previewAssets 
      : previewAssets.filter(asset => 
          asset.assetType.id.toString() === previewSelectedAssetType.toString()
        );
    
    // 미리보기용 선택된 자산 정보 가져오기
    const getPreviewSelectedAssetInfo = () => {
      if (previewSelectedAssetType === 'all') {
        // 전체 자산의 합계 계산
        const totalAmount = previewAssetBalances.reduce((sum, balance) => sum + balance.amount, 0);
        const totalAvailable = previewAssetBalances.reduce((sum, balance) => sum + balance.availableAmount, 0);
        
        return {
          displayName: t('원'),
          amount: totalAmount,
          availableAmount: totalAvailable
        };
      }
      
      // 선택한 자산 유형의 정보 찾기
      const selectedAsset = previewAssetBalances.find(
        balance => balance.assetTypeId.toString() === previewSelectedAssetType.toString()
      );
      
      if (!selectedAsset) {
        return { displayName: t('원'), amount: 0, availableAmount: 0 };
      }
      
      // 회사별 표시 이름 가져오기 (첫 번째 회사 정보 사용)
      const companyIds = Object.keys(selectedAsset.companyDisplayNames || {});
      const displayInfo = companyIds.length > 0 
        ? selectedAsset.companyDisplayNames[companyIds[0]] 
        : { displayName: selectedAsset.assetName };
      
      return {
        displayName: displayInfo.displayName || selectedAsset.assetName,
        amount: selectedAsset.amount,
        availableAmount: selectedAsset.availableAmount,
        iconUrl: selectedAsset.iconUrl
      };
    };
    
    // 미리보기용 선택된 자산 정보
    const previewSelectedAssetInfo = getPreviewSelectedAssetInfo();
    
    // 미리보기용 자산 유형 변경 핸들러
    const handlePreviewAssetTypeChange = (e) => {
      setPreviewSelectedAssetType(e.target.value);
    };
    
    // 미리보기용 출금 폼 토글 처리
    const togglePreviewWithdrawForm = () => {
      setPreviewShowWithdrawForm(!previewShowWithdrawForm);
    };
    
    // 미리보기용 출금 폼 렌더링
    const renderPreviewWithdrawForm = () => {
      // 선택된 자산 정보
      const assetInfo = getPreviewSelectedAssetInfo();
      
      return (
        <div className="withdraw-form-container">
          <h3>{t("자산 출금 요청")}</h3>
          <form className="withdraw-form" onSubmit={(e) => { e.preventDefault(); alert(t('미리보기 모드에서는 요청이 전송되지 않습니다.')); }}>
            {/* 자산 유형 선택기 */}
            <div className="form-group">
              <label htmlFor="preview-withdraw-asset-type-select">{t("자산 유형")}</label>
              <select 
                id="preview-withdraw-asset-type-select" 
                value={previewSelectedAssetType} 
                onChange={handlePreviewAssetTypeChange}
              >
                {previewAssetTypes.filter(type => type.id !== 'all').map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name} {type.displayName && type.displayName !== type.name ? `(${type.displayName})` : ''}
                  </option>
                ))}
              </select>
              <div className="point-available">
                {t("사용 가능한 자산")}: {assetInfo.availableAmount.toLocaleString()} {assetInfo.displayName}
              </div>
              <div className="point-available">
                {t("잔액")}: {assetInfo.amount.toLocaleString()} {assetInfo.displayName}
              </div>
            </div>
            
            <div className="form-group">
              <label>{t("출금 금액")}</label>
              <input
                type="text"
                placeholder={t("출금할 금액을 입력하세요")}
              />
            </div>
            
            <div className="form-group">
              <label>{t("출금 주소")}</label>
              <input
                type="text"
                placeholder={t("출금할 주소를 입력하세요")}
              />
              <div className="address-helper">
                {t("선택한 자산의 올바른 주소 형식을 입력해주세요.")}
              </div>
            </div>
            
            <div className="form-group">
              <label>{t("메모 (선택사항)")}</label>
              <input
                type="text"
                placeholder={t("메모를 입력하세요")}
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn"
                onClick={togglePreviewWithdrawForm}
              >
                {t("취소")}
              </button>
              <button 
                type="submit" 
                className="submit-btn"
              >
                {t("출금 요청")}
              </button>
            </div>
          </form>
        </div>
      );
    };
    
    // 미리보기용 환전 모달 토글 처리
    const togglePreviewExchangeModal = (assetInfo) => {
      setPreviewShowExchangeModal(!previewShowExchangeModal);
    };
    
    // 미리보기용 환전 가능 여부 확인
    const isPreviewAssetExchangeable = (assetInfo) => {
      if (!assetInfo || !assetInfo.exchange) return false;
      return assetInfo.exchange.enabled && assetInfo.availableAmount > assetInfo.exchange.withdrawMinRest;
    };
    
    // 미리보기용 환전 모달 렌더링
    const renderPreviewExchangeModal = () => {
      // 선택된 자산 정보
      const selectedAsset = previewAssetBalances.find(
        balance => balance.assetTypeId.toString() === previewSelectedAssetType.toString()
      ) || previewAssetBalances[0];
      
      // 환전 가능한 대상 자산 목록
      const targetAssets = previewAssetBalances.filter(
        asset => asset.assetTypeId !== selectedAsset.assetTypeId && asset.exchange?.enabled
      );
      
      return (
        <div className="modal-overlay">
          <div className="exchange-modal">
            <div className="modal-header">
              <h3>{t("자산 환전")}</h3>
              <button 
                className="close-button"
                onClick={togglePreviewExchangeModal}
              >&times;</button>
            </div>
            <form className="exchange-form" onSubmit={(e) => { e.preventDefault(); alert(t('미리보기 모드에서는 요청이 전송되지 않습니다.')); }}>
              {/* 환전 출발 자산 정보 */}
              <div className="form-group">
                <label>{t("환전 출발 자산")}</label>
                <div className="asset-info-box">
                  <div className="asset-icon">
                    <img 
                      src={selectedAsset.iconUrl || '#'} 
                      alt={selectedAsset.assetName} 
                      className="asset-icon" 
                      width="16" 
                      height="16" 
                    />
                  </div>
                  <div className="asset-name">
                    {selectedAsset.assetName}
                  </div>
                  <div className="asset-available">
                    {t("사용 가능")}: {selectedAsset.availableAmount.toLocaleString()} {selectedAsset.companyDisplayNames["1"].displayName}
                  </div>
                </div>
              </div>
              
              {/* 환전 도착 자산 정보 */}
              <div className="form-group">
                <label>{t("환전 도착 자산")}</label>
                <select 
                  id="preview-exchange-to-asset"
                >
                  <option value="">{t("환전할 자산 선택")}</option>
                  {targetAssets.map(asset => (
                    <option key={asset.assetTypeId} value={asset.assetTypeId}>
                      {asset.assetName} {asset.companyDisplayNames["1"].displayName}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* 환전율 정보 */}
              <div className="exchange-rate-info">
                <p>{t("환전율")}: 1 {selectedAsset.companyDisplayNames["1"].displayName} = 0.5 {targetAssets.length > 0 ? targetAssets[0].companyDisplayNames["1"].displayName : ''}</p>
              </div>
              
              {/* 환전 금액 */}
              <div className="form-group">
                <label>{t("환전 금액")}</label>
                <input
                  type="text"
                  placeholder={t("환전할 금액을 입력하세요")}
                  className="input-styled"
                />
                {selectedAsset.exchange?.withdrawMinRest > 0 && (
                  <div className="withdraw-limit-info">
                    {t("최소 잔액 유지")}: {selectedAsset.exchange.withdrawMinRest.toLocaleString()} {selectedAsset.companyDisplayNames["1"].displayName}
                  </div>
                )}
                
                {/* 수수료 및 실제 환전 금액 표시 */}
                <div className="fee-calculation">
                  <div className="fee-info">
                    {t("수수료")}: 100 {selectedAsset.companyDisplayNames["1"].displayName}
                  </div>
                  <div className="receive-info">
                    {t("환전 결과")}: 450 {targetAssets.length > 0 ? targetAssets[0].companyDisplayNames["1"].displayName : ''}
                  </div>
                </div>
              </div>
              
              {/* 환전 사유 */}
              <div className="form-group">
                <label>{t("환전 사유 (선택사항)")}</label>
                <input
                  type="text"
                  placeholder={t("환전 사유를 입력하세요")}
                  className="input-styled"
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={togglePreviewExchangeModal}
                >
                  {t("취소")}
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                >
                  {t("환전하기")}
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    };
    
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="mileage-history-title">{t(title)}</h2>
              
            </div>
          </div>
          
          {/* 미리보기 출금 폼 */}
          {previewShowWithdrawForm && renderPreviewWithdrawForm()}
          
          {/* 미리보기 환전 모달 */}
          {previewShowExchangeModal && renderPreviewExchangeModal()}
          
          {/* 미리보기 자산 카드 */}
          <div className="asset-balance-list">
            {previewAssetBalances.map(asset => {
              const displayInfo = asset.companyDisplayNames["1"];
              const isExchangeable = isPreviewAssetExchangeable(asset);
              
              return (
                <div 
                  key={asset.assetTypeId} 
                  className={`asset-balance-card ${previewSelectedAssetType === asset.assetTypeId.toString() ? 'active' : ''}`}
                  onClick={() => setPreviewSelectedAssetType(asset.assetTypeId.toString())}
                >
                  <div className="asset-balance-header">
                    <div className="asset-balance-icon">
                      <img 
                        src={asset.iconUrl || '#'} 
                        alt={asset.assetName} 
                        className="asset-icon" 
                        width="16" 
                        height="16" 
                      />
                    </div>
                    <div className="asset-balance-name">
                      {asset.assetName}
                    </div>
                    <div className="asset-actions">
                      {/* 환전 버튼 */}
                      {isExchangeable && (
                        <button 
                          className="asset-exchange-button"
                          onClick={(e) => {
                            e.stopPropagation(); // 버블링 방지
                            setPreviewSelectedAssetType(asset.assetTypeId.toString());
                            togglePreviewExchangeModal();
                          }}
                          title={t("환전하기")}
                        >
                          {t("환전")}
                        </button>
                      )}
                      {/* 출금 버튼 */}
                      <button 
                        className="asset-withdraw-button"
                        onClick={(e) => {
                          e.stopPropagation(); // 버블링 방지
                          setPreviewSelectedAssetType(asset.assetTypeId.toString());
                          togglePreviewWithdrawForm();
                        }}
                        title={t("출금 요청하기")}
                      >
                        {t("출금")}
                      </button>
                    </div>
                  </div>
                  <div className="asset-balance-body">
                    <div className="asset-balance-amount">
                      <span className="amount-value">{asset.availableAmount.toLocaleString()}</span>
                      <span className="amount-unit">{displayInfo.displayName}</span>
                    </div>
                    <div className="asset-total-amount">
                      {t("총 잔액")}: {asset.amount.toLocaleString()} {displayInfo.displayName}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mileage-history-list">
            <div className="mileage-history-header">
              <div className="mh-col date">
                {t("날짜")} <button className="sort-btn">↓</button>
              </div>
              <div className="mh-col mileage-description">
                {t("내용")}
              </div>
              <div className="mh-col amount">
                {t("금액")} <button className="sort-btn">↓</button>
              </div>
            </div>
            
            {filteredPreviewAssets.length > 0 ? (
              filteredPreviewAssets.map((asset) => {
                const isPositive = asset.amount > 0;
                const displayUnit = asset.companyAssetType?.displayName || "";
                
                return (
                  <div className="mileage-item" key={asset.id}>
                    <div className="mh-col date">
                      {formatDate(asset.createdAt)}
                    </div>
                    <div className="mh-col mileage-description">
                      {asset.desc}
                      {asset.status === 'PENDING' && (
                        <span className="status-badge pending">{t("승인 대기중")}</span>
                      )}
                      {asset.orderId && (
                        <span className="order-link">
                          {t("주문번호")}: <a href={`/orders/${asset.orderId}`}>{asset.orderId}</a>
                        </span>
                      )}
                    </div>
                    <div className={`mh-col amount ${isPositive ? 'positive' : 'negative'}`}>
                      {formatAmount(asset.amount)} {displayUnit}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state">
                <p>{emptyText}</p>
              </div>
            )}
          </div>
          
          <div className="pagination-container">
            <button className="pagination-button prev" disabled>{t("이전")}</button>
            <button className="pagination-button active">1</button>
            <button className="pagination-button next" disabled>{t("다음")}</button>
          </div>
        </div>
      </div>
    );
  }

  // 환전 모달 컴포넌트
  const renderExchangeModal = () => {
    // 모달이 닫혀있으면 렌더링하지 않음
    if (!showExchangeModal) return null;
    
    // 환전 가능한 대상 자산 목록
    const targetAssets = getExchangeableTargetAssets(exchangeFromAsset?.assetTypeId);
    
    // 출발 자산 정보 표시
    const fromAssetDisplayName = (exchangeFromAsset?.withdrawal?.companySettings?.[0]?.displayName) || '';
    const fromAssetAvailableAmount = exchangeFromAsset?.actualAvailableAmount || exchangeFromAsset?.availableAmount || 0;
    
    // 환전 환율 정보 표시 (환전 대상 자산이 선택된 경우)
    let exchangeRate = 1;
    if (exchangeFromAsset && exchangeToAsset) {
      // 회사 ID 가져오기
      const companyIds = Object.keys(exchangeFromAsset.companyDisplayNames || {});
      const companyId = selectedCompanyId || (companyIds.length > 0 ? companyIds[0] : null);
      
      // 해당 회사의 환전 설정 찾기
      const fromCompanySettings = exchangeFromAsset.exchange.companySettings && 
        exchangeFromAsset.exchange.companySettings.find(cs => cs.companyId.toString() === companyId);
      
      const toCompanySettings = exchangeToAsset.exchange.companySettings &&
        exchangeToAsset.exchange.companySettings.find(cs => cs.companyId.toString() === companyId);
      
      // 환율 계산
      const fromValue = fromCompanySettings?.exchangeSettings?.value || 1;
      const toValue = toCompanySettings?.exchangeSettings?.value || 1;
      
      exchangeRate = fromValue / toValue;
    }
    
    return (
      <div className="modal-overlay">
        <div className="exchange-modal">
          <div className="modal-header">
            <h3>{t("자산 환전")}</h3>
            <button 
              className="close-button"
              onClick={closeExchangeModal}
              disabled={exchangeProcessing}
            >&times;</button>
          </div>
          <form className="exchange-form" onSubmit={handleExchangeRequest}>
            {/* 환전 출발 자산 정보 */}
            <div className="form-group">
              <label>{t("환전 출발 자산")}</label>
              <div className="asset-info-box">
                <div className="asset-icon">
                  <AssetIcon iconUrl={exchangeFromAsset?.iconUrl} assetName={exchangeFromAsset?.assetName} />
                </div>
                <div className="asset-name">
                  {exchangeFromAsset?.assetName}
                </div>
                <div className="asset-available">
                  {t("사용 가능")}: {fromAssetAvailableAmount.toLocaleString()} {fromAssetDisplayName}
                </div>
              </div>
            </div>
            
            {/* 환전 도착 자산 정보 */}
            <div className="form-group">
              <label htmlFor="exchange-to-asset">{t("환전 도착 자산")}</label>
              <div className="custom-select-wrapper">
                <select 
                  id="exchange-to-asset" 
                  value={exchangeToAsset?.assetTypeId || ''}
                  onChange={handleExchangeToAssetChange}
                  disabled={exchangeProcessing || targetAssets.length <= 0}
                  required
                  className="custom-select"
                >
                  <option value="">{t("환전할 자산 선택")}</option>
                  {targetAssets.map(asset => (
                    <option key={asset.assetTypeId} value={asset.assetTypeId}>
                      {asset.assetName} {asset.displayName && asset.displayName !== asset.assetName ? `(${asset.displayName})` : ''}
                    </option>
                  ))}
                </select>
                <div className="select-arrow">
                  <ArrowDownIcon />
                </div>
              </div>
              {exchangeToAsset && (
                <div className="asset-info-box to-asset">
                  <div className="asset-icon">
                    <AssetIcon iconUrl={exchangeToAsset.iconUrl} assetName={exchangeToAsset.assetName} />
                  </div>
                  <div className="asset-name">
                    {exchangeToAsset.assetName}
                  </div>
                  <div className="asset-available">
                    {t("잔액")}: {(exchangeToAsset.amount || 0).toLocaleString()} {exchangeToAsset?.withdrawal?.companySettings?.[0]?.displayName || ''}
                  </div>
                </div>
              )}
            </div>
            
            {/* 환전율 정보 */}
            {exchangeFromAsset && exchangeToAsset && (
              <div className="exchange-rate-info">
                <p>{t("환전율")}: 1 {exchangeFromAsset.displayName} = {exchangeRate.toFixed(4)} {exchangeToAsset.displayName}</p>
              </div>
            )}
            
            {/* 환전 금액 */}
            <div className="form-group">
              <label>{t("환전 금액")}</label>
              <input
                type="text"
                value={exchangeAmount}
                onChange={handleExchangeAmountChange}
                placeholder={t("환전할 금액을 입력하세요")}
                disabled={exchangeProcessing || !exchangeToAsset}
                required
                className="input-styled"
              />
              {exchangeFromAsset?.exchange?.withdrawMinRest > 0 && (
                <div className="withdraw-limit-info">
                  {t("최소 잔액 유지")}: {exchangeFromAsset.exchange.withdrawMinRest.toLocaleString()} {fromAssetDisplayName}
                </div>
              )}
              
              {/* 수수료 및 실제 환전 금액 표시 */}
              {exchangeAmount && Number(exchangeAmount) > 0 && exchangeToAsset && (
                <div className="fee-calculation">
                  <div className="fee-info">
                    {t("수수료")}: {exchangeFeeAmount.toLocaleString()} {fromAssetDisplayName}
                  </div>
                  <div className="receive-info">
                    {t("환전 결과")}: {exchangeReceiveAmount.toFixed(2).toLocaleString()} {exchangeToAsset.displayName || t('원')}
                  </div>
                </div>
              )}
            </div>
            
            {/* 환전 사유 */}
            <div className="form-group">
              <label>{t("환전 사유 (선택사항)")}</label>
              <input
                type="text"
                value={exchangeReason}
                onChange={(e) => setExchangeReason(e.target.value)}
                placeholder={t("환전 사유를 입력하세요")}
                disabled={exchangeProcessing}
                className="input-styled"
              />
            </div>
            
            {exchangeDisabledMessage && (
              <div className="exchange-disabled-message">
                {exchangeDisabledMessage}
              </div>
            )}
            
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn"
                onClick={closeExchangeModal}
                disabled={exchangeProcessing}
              >
                {t("취소")}
              </button>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={
                  exchangeProcessing || 
                  !exchangeToAsset ||
                  !exchangeAmount ||
                  isNaN(Number(exchangeAmount)) ||
                  Number(exchangeAmount) <= 0 ||
                  Number(exchangeAmount) > fromAssetAvailableAmount ||
                  (fromAssetAvailableAmount - Number(exchangeAmount)) < (exchangeFromAsset?.exchange?.withdrawMinRest || 0)
                }
              >
                {exchangeProcessing ? t('처리 중...') : t('환전하기')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  // 입금 모달 컴포넌트
  const renderDepositModal = () => {
    if (!showDepositModal || !depositAsset || !depositAsset.wallet) return null;
    
    const walletAddress = depositAsset.wallet.address;
    const networkName = depositAsset.wallet.blockchainNetworkId === 1 ? 'TRON' : t('알 수 없음');
    
    // 회사별 표시 이름 가져오기
    const companyIds = Object.keys(depositAsset.companyDisplayNames || {});
    const displayInfo = companyIds.length > 0 
      ? depositAsset.companyDisplayNames[companyIds[0]] 
      : { displayName: depositAsset.assetName };
    
    return (
      <div className="modal-overlay">
        <div className="deposit-modal" style={{ width: '90%', maxWidth: '500px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 0 20px rgba(0,0,0,0.3)' }}>
          <div className="modal-header" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '15px 20px', 
            borderBottom: '1px solid #eee',
            backgroundColor: '#f8f9fa'
          }}>
            <h3 style={{ margin: 0, color: '#333' }}>{t("자산 입금")}</h3>
            <button 
              className="close-button"
              onClick={closeDepositModal}
              style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#555' }}
            >&times;</button>
          </div>
          <div className="modal-content" style={{ padding: '20px' }}>

            <div className="warning-message" style={{ 
              color: '#e53935', 
              fontSize: '14px', 
              padding: '15px', 
              backgroundColor: 'rgba(229, 57, 53, 0.1)',
              borderRadius: '6px',
              marginBottom: '20px',
              border: '1px solid rgba(229, 57, 53, 0.3)',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'flex-start'
            }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>⚠️</span>
              <span>
                {t("주의: 반드시 올바른 네트워크를 확인하고 입금하세요. 잘못된 네트워크로 입금 시 자산이 손실될 수 있습니다.")}
              </span>
            </div>

            <div className="asset-info" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
              <div className="asset-icon" style={{ marginRight: '10px' }}>
                <AssetIcon iconUrl={depositAsset.iconUrl} assetName={depositAsset.assetName} />
              </div>
              <div>
                <div className="asset-name" style={{ fontWeight: 'bold' }}>{depositAsset.assetName}</div>
                <div className="asset-display-name">{displayInfo.displayName}</div>
              </div>
            </div>
            
            <div className="wallet-info" style={{ marginBottom: '20px' }}>
              <div className="network-info" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t("네트워크")}:</label>
                <div style={{ 
                  padding: '10px 15px', 
                  backgroundColor: '#f0f8ff', 
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  border: '1px solid #d1e5ff'
                }}>
                  {networkName}
                </div>
              </div>
              
              <div className="address-info">
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t("입금 주소")}:</label>
                <div style={{ 
                  position: 'relative', 
                  padding: '12px 40px 12px 15px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '6px',
                  wordBreak: 'break-all',
                  marginBottom: '10px',
                  border: '1px solid #e0e0e0',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}>
                  {walletAddress}
                  <button 
                    onClick={() => copyWalletAddress(walletAddress)}
                    style={{ 
                      position: 'absolute', 
                      right: '8px', 
                      top: '8px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '5px',
                      color: '#0066cc'
                    }}
                    title={t("주소 복사")}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="qr-code" style={{ 
              textAlign: 'center', 
              marginBottom: '20px', 
              padding: '15px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                {t("QR 코드 스캔")}
              </div>
              <div style={{ 
                width: '200px', 
                height: '200px', 
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(walletAddress)}`} 
                  alt={t("지갑 주소 QR 코드")}
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                />
              </div>
            </div>
            
            <div className="note" style={{ fontSize: '13px', color: '#666' }}>
              {t("입금 완료 후 블록체인 네트워크 상황에 따라 반영까지 시간이 소요될 수 있습니다.")}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // 실제 데이터 표시 (API 응답)
  return (
    <div className={className} id={id} style={style}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h2 className="mileage-history-title">{title}</h2>
            
            
          </div>
        </div>
        
        {showWithdrawForm && renderWithdrawForm()}
        {showExchangeModal && renderExchangeModal()}
        {showDepositModal && renderDepositModal()}
        
        {apiError && (
          <div className="error-message">
            <p>{apiError}</p>
          </div>
        )}
        
        {isLoading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            {/* 로딩 텍스트 제거 */}
          </div>
        ) : (
          <>
            {/* 자산 리스트 추가 (현재 보유 중인 자산을 카드 형태로 표시) */}
            {userAssetBalances.length > 0 && (
              <div className="asset-balance-list">
                {userAssetBalances.map(asset => {
                  // 각 자산의 출금 가능 여부 확인
                  const withdrawalStatus = getAssetWithdrawalStatus(asset.assetTypeId);
                  
                  // 각 자산의 환전 가능 여부를 직접 검사 (함수 대신 인라인으로 처리)
                  const isExchangeEnabled = (() => {
                    // 환전 설정이 없거나 비활성화된 경우
                    if (!asset.exchange || !asset.exchange.enabled || !asset.exchange.fromEnabled) {
                      return false;
                    }
                    
                    // 사용 가능 금액 확인
                    const availableAmount = asset.actualAvailableAmount || asset.availableAmount || 0;
                    const minRest = asset.exchange.withdrawMinRest || 0;
                    
                    // // 최소 잔액보다 작으면 환전 불가
                    // if (availableAmount <= minRest) {
                    //   return false;
                    // }
                    
                    // 환전 가능한 대상 자산 확인
                    const targetAssets = getExchangeableTargetAssets(asset.assetTypeId);
                    if (targetAssets.length === 0) {
                      return false;
                    }
                    
                    return true;
                  })();
                  
                  // 회사별 표시 이름 가져오기
                  const companyIds = Object.keys(asset.companyDisplayNames || {});
                  const displayInfo = companyIds.length > 0 
                    ? asset.companyDisplayNames[companyIds[0]] 
                    : { displayName: asset.assetName };
                  
                  const availableAmount = asset.actualAvailableAmount || asset.availableAmount || 0;
                  
                  return (
                    <div 
                      key={asset.assetTypeId} 
                      className={`asset-balance-card ${selectedAssetType === asset.assetTypeId.toString() ? 'active' : ''}`}
                      onClick={() => setSelectedAssetType(asset.assetTypeId.toString())}
                    >
                      <div className="asset-balance-header">
                        <div className="asset-balance-icon">
                          <AssetIcon iconUrl={asset.iconUrl} assetName={asset.assetName} />
                        </div>
                        <div className="asset-balance-name">
                          {asset.assetName}
                        </div>
                        <div className="asset-actions" style={{ display: 'flex', gap: '5px', marginLeft: 'auto' }}>
                          {/* 입금 버튼 추가 */}
                          {(() => {

                            if (!asset.wallet || !asset.wallet.address) return null;
                            
                            return (
                              <button 
                                className="asset-deposit-button"
                                style={{ 
                                  backgroundColor: '#28a745', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '4px', 
                                  padding: '4px 8px', 
                                  fontSize: '0.8rem', 
                                  cursor: 'pointer',
                                  position: 'static'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation(); // 버블링 방지
                                  console.log('자산 카드에서 입금 버튼 클릭:', {
                                    assetId: asset.assetTypeId,
                                    assetName: asset.assetName,
                                    wallet: asset.wallet
                                  });
                                  openDepositModal(asset);
                                }}
                                title={t("입금하기")}
                              >
                                {t("입금")}
                              </button>
                            );
                          })()}
                          
                          {/* 환전 버튼 추가 */}
                          {(() => {
                            // 각 자산의 환전 가능 여부를 직접 검사
                            const isExchangeEnabled = (() => {
                              // 환전 설정이 없거나 비활성화된 경우
                              if (!asset.exchange || !asset.exchange.enabled) {
                                return false;
                              }
                              
                              // 사용 가능 금액 확인
                              const availableAmount = asset.actualAvailableAmount || asset.availableAmount || 0;
                              // if (availableAmount <= 0) {
                              //   return false;
                              // }
                              
                              // 회사별 환전 설정 확인
                              if (asset.exchange.companySettings && asset.exchange.companySettings.length > 0) {
                                // 우선 선택된 회사 확인
                                const companyIds = Object.keys(asset.companyDisplayNames || {});
                                const companyId = selectedCompanyId || (companyIds.length > 0 ? companyIds[0] : null);
                                
                                if (companyId) {
                                  const companySettings = asset.exchange.companySettings.find(
                                    company => company.companyId.toString() === companyId
                                  );
                                  
                                  if (companySettings) {
                                    return companySettings.exchangeEnabled && companySettings.exchangeFromEnabled;
                                  }
                                }
                                
                                // 선택된 회사가 없으면 하나라도 환전 가능한 회사가 있는지 확인
                                return asset.exchange.companySettings.some(
                                  company => company.exchangeEnabled && company.exchangeFromEnabled
                                );
                              }
                              
                              return true;
                            })();
                            
                            if (!isExchangeEnabled) return null;
                            
                            return (
                              <button 
                                className="asset-exchange-button"
                                style={{ 
                                  backgroundColor: '#0066cc', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '4px', 
                                  padding: '4px 8px', 
                                  fontSize: '0.8rem', 
                                  cursor: 'pointer',
                                  position: 'static'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation(); // 버블링 방지
                                  console.log('자산 카드에서 환전 버튼 클릭:', {
                                    assetId: asset.assetTypeId,
                                    assetName: asset.assetName,
                                    exchange: asset.exchange
                                  });
                                  openExchangeModal(asset);
                                }}
                                title={t("환전하기")}
                              >
                                {t("환전")}
                              </button>
                            );
                          })()}
                          
                          {/* 출금 버튼 */}
                          {(() => {
                            // 각 자산의 출금 가능 여부 확인
                            const withdrawalStatus = getAssetWithdrawalStatus(asset.assetTypeId);
                            
                            if (!withdrawalStatus.enabled) return null;
                            
                            return (
                              <button 
                                className="asset-withdraw-button"
                                style={{ 
                                  backgroundColor: '#4a90e2', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '4px', 
                                  padding: '4px 8px', 
                                  fontSize: '0.8rem', 
                                  cursor: 'pointer',
                                  position: 'static'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation(); // 버블링 방지
                                  console.log('자산 카드에서 출금 버튼 클릭:', {
                                    assetId: asset.assetTypeId,
                                    assetName: asset.assetName,
                                    withdrawal: asset.withdrawal
                                  });
                                  setSelectedAssetType(asset.assetTypeId.toString());
                                  toggleWithdrawForm();
                                }}
                                title={t("출금 요청하기")}
                              >
                                {t("출금")}
                              </button>
                            );
                          })()}
                          {/* 투자 버튼: companyId=24, assetTypeId=4일 때만 노출 */}
                          {(() => {
                            // companyId는 회사별 표시 이름에서 추출 (companyIds[0])
                            const companyIds = Object.keys(asset.companyDisplayNames || {});
                            const companyId = companyIds.length > 0 ? Number(companyIds[0]) : null;
                            if (companyId === 24 && Number(asset.assetTypeId) === 4) {
                              return (
                                <button
                                  className="asset-invest-button"
                                  style={{
                                    backgroundColor: '#ff9800',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    position: 'static'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = '/getmembership';
                                  }}
                                  title={t('투자하기')}
                                >
                                  {t('투자')}
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                      <div className="asset-balance-body">
                        <div className="asset-balance-amount">
                          <span className="amount-value">{availableAmount.toLocaleString()}</span>
                          <span className="amount-unit">{displayInfo.displayName}</span>
                        </div>
                        {asset.pendingWithdrawals > 0 && (
                          <div className="asset-pending-amount">
                            {t("출금 대기")}: {asset.pendingWithdrawals.toLocaleString()} {displayInfo.displayName}
                          </div>
                        )}
                        <div className="asset-total-amount">
                          {t("총 잔액")}: {asset.amount.toLocaleString()} {displayInfo.displayName}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="mileage-history-list">
              <div className="mileage-history-header">
                <div className="mh-col date">
                  {t("날짜")}
                </div>
                <div className="mh-col mileage-description">
                  {t("내용")}
                </div>
                <div className="mh-col amount">
                  {t("금액")}
                </div>
              </div>
              
              {filteredAssetData && filteredAssetData.length > 0 ? (
                filteredAssetData.map((asset) => {
                  const isPositive = asset.amount > 0;
                  // 각 거래에 맞는 표시 단위 사용
                  const displayUnit = asset.companyAssetType?.displayName || "";
                  
                  return (
                    <div className="mileage-item" key={asset.id}>
                      <div className="mh-col date">
                        {formatDate(asset.createdAt)}
                      </div>
                      <div className="mh-col mileage-description">
                        {asset.desc}
                        {asset.status === 'PENDING' && (
                          <span className="status-badge pending">{t("승인 대기중")}</span>
                        )}
                        {asset.orderId && (
                          <span className="order-link">
                            {t("주문번호")}: <a href={`/orders/${asset.orderId}`}>{asset.orderId}</a>
                          </span>
                        )}
                      </div>
                      <div className={`mh-col amount ${isPositive ? 'positive' : 'negative'}`}>
                        {formatAmount(asset.amount)} {displayUnit}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <p>{emptyText}</p>
                </div>
              )}
            </div>
            
            {filteredAssetData && filteredAssetData.length > 0 && (
              <Pagination 
                currentPage={page} 
                totalItems={totalCount} 
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssetHistoryComponent; 